/**
 * UnitDisclosuresPage
 * Lists all required disclosures for a unit, showing status and staleness.
 * Route: /units/:unitId/disclosures
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  listUnitDisclosures,
  getUnit,
  type UnitDisclosureListItem,
  type ListDisclosuresResponse,
  type UnitWithRelations,
} from '../lib/api';
import { DisclosureCard } from '../components/DisclosureCard';
import { getDisclosureStatus, formatDisclosureId } from '../components/disclosure-utils';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { JurisdictionCoverageStatement } from '../components/JurisdictionCoverageStatement';
import { LocalRuleNote } from '../components/LocalRuleNote';
import { Skeleton } from '../components/ui/skeleton';
import { formatUnitLabel } from '@resida/shared';

// ============================================================================
// PAGE COMPONENT
// ============================================================================

export function UnitDisclosuresPage(): JSX.Element {
  const { unitId } = useParams<{ unitId: string }>();

  const [unit, setUnit] = useState<UnitWithRelations | null>(null);
  const [disclosures, setDisclosures] = useState<UnitDisclosureListItem[]>([]);
  const [coverage, setCoverage] = useState<ListDisclosuresResponse['coverage'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!unitId) return;
    try {
      setIsLoading(true);
      setError(null);
      const [unitRes, disclosuresRes] = await Promise.all([
        getUnit(unitId),
        listUnitDisclosures(unitId),
      ]);
      setUnit(unitRes.unit);
      setDisclosures(disclosuresRes.disclosures);
      setCoverage(disclosuresRes.coverage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load disclosures');
    } finally {
      setIsLoading(false);
    }
  }, [unitId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) {
    return (
      <div
        className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
        data-testid="disclosures-loading"
      >
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64 mb-6" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
        data-testid="disclosures-error"
      >
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive m-0">{error}</p>
          <Button
            variant="link"
            onClick={loadData}
            className="mt-2 text-sm text-destructive p-0 h-auto"
            data-testid="disclosures-retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const unitLabel = unit
    ? (formatUnitLabel(unit.unitNumber) ?? unit.property?.name ?? 'Unit')
    : 'Unit';

  const requiredDisclosures = disclosures.filter((d) => d.required);
  const optionalDisclosures = disclosures.filter((d) => !d.required);
  const allRequiredComplete = requiredDisclosures.every(
    (d) => !d.hasFields || d.record?.status === 'COMPLETE'
  );
  const anyStale = disclosures.some((d) => d.isStale);
  const missingCount = requiredDisclosures.filter((d) => d.hasFields && !d.record).length;
  const draftCount = requiredDisclosures.filter(
    (d) => d.hasFields && d.record?.status === 'DRAFT'
  ).length;

  return (
    <div
      className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
      data-testid="unit-disclosures-page"
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 mb-4 text-sm">
        <Link to="/units" className="text-primary hover:text-primary/80 no-underline">
          Units
        </Link>
        <span className="text-muted-foreground">/</span>
        {unitId && (
          <>
            <Link
              to={`/units/${unitId}`}
              className="text-primary hover:text-primary/80 no-underline"
            >
              {unitLabel}
            </Link>
            <span className="text-muted-foreground">/</span>
          </>
        )}
        <span className="text-muted-foreground">Disclosures</span>
      </div>

      <PageHeader
        title="Required Disclosures"
        subtitle={`${unitLabel} — Manage disclosure records for lease generation`}
        className={coverage ? 'mb-4' : 'mb-6'}
      />

      {coverage && (
        <div className="mb-6 flex flex-col gap-2">
          <JurisdictionCoverageStatement coverage={coverage} subject="disclosure set" />
          <LocalRuleNote coverage={coverage} category="required_disclosures" />
        </div>
      )}

      {/* Readiness Banner */}
      {allRequiredComplete && !anyStale ? (
        <div
          className="flex items-center gap-3 px-4 py-3 mb-6 bg-success/10 border border-success/20 rounded-lg"
          data-testid="disclosures-ready-banner"
          role="status"
          aria-live="polite"
        >
          <svg
            className="w-5 h-5 text-success shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-medium text-success">
            All disclosures complete — this unit is ready for lease generation.
          </span>
        </div>
      ) : (
        <div
          className="flex flex-col gap-1 px-4 py-3 mb-6 bg-warning/10 border border-warning/20 rounded-lg"
          data-testid="disclosures-incomplete-banner"
          role="status"
          aria-live="polite"
        >
          <span className="text-sm font-medium text-warning">
            {missingCount > 0 && `${missingCount} disclosure${missingCount !== 1 ? 's' : ''} missing. `}
            {draftCount > 0 && `${draftCount} disclosure${draftCount !== 1 ? 's' : ''} in draft. `}
            {anyStale && 'Some disclosures may be stale. '}
            Complete all required disclosures before generating a lease.
          </span>
        </div>
      )}

      {/* Required Disclosures */}
      {requiredDisclosures.length > 0 && (
        <section aria-labelledby="required-disclosures-heading" className="mb-8">
          <h2
            id="required-disclosures-heading"
            className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3"
          >
            Required by Jurisdiction
          </h2>
          <div className="flex flex-col gap-3" data-testid="required-disclosures-list">
            {requiredDisclosures.map((item) => {
              const status = getDisclosureStatus(item);
              return (
                <DisclosureCard key={item.disclosureId} item={item} className="bg-card border border-border rounded-lg">
                  {status !== 'Included' && (
                    <Button asChild size="sm" className="shrink-0" data-testid={`edit-disclosure-${item.disclosureId}`} aria-label={`Edit ${formatDisclosureId(item.disclosureId)} disclosure`}>
                      <Link to={`/units/${unitId}/disclosures/${encodeURIComponent(item.disclosureId)}`} className="no-underline min-h-[44px] flex items-center">
                        {status === 'Missing' ? 'Add' : 'Edit'}
                      </Link>
                    </Button>
                  )}
                </DisclosureCard>
              );
            })}
          </div>
        </section>
      )}

      {/* Optional Disclosures */}
      {optionalDisclosures.length > 0 && (
        <section aria-labelledby="optional-disclosures-heading">
          <h2
            id="optional-disclosures-heading"
            className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3"
          >
            Optional Disclosures
          </h2>
          <div className="flex flex-col gap-3" data-testid="optional-disclosures-list">
            {optionalDisclosures.map((item) => {
              const status = getDisclosureStatus(item);
              return (
                <DisclosureCard key={item.disclosureId} item={item} className="bg-card border border-border rounded-lg">
                  {status !== 'Included' && (
                    <Button asChild size="sm" variant="outline" className="shrink-0" data-testid={`edit-disclosure-${item.disclosureId}`}>
                      <Link to={`/units/${unitId}/disclosures/${encodeURIComponent(item.disclosureId)}`} className="no-underline min-h-[44px] flex items-center">
                        {status === 'Missing' ? 'Add' : 'Edit'}
                      </Link>
                    </Button>
                  )}
                </DisclosureCard>
              );
            })}
          </div>
        </section>
      )}

      {/* Empty state */}
      {disclosures.length === 0 && (
        <div
          className="py-12 text-center bg-muted/40 rounded-lg border border-border"
          data-testid="disclosures-empty"
        >
          <p className="text-base text-muted-foreground mb-1">No disclosures required</p>
          <p className="text-sm text-muted-foreground/70">
            No disclosure requirements found for this unit's jurisdiction.
          </p>
        </div>
      )}
    </div>
  );
}
